import * as React from 'react';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { VerificationCodeInput } from '../VerificationCodeInput';

interface Props {
  onSubmit: (data: { phoneCode: string }) => void;
  isConfirmed: boolean;
  phone: string;
  onSendCodeAgainClick: (event: any) => void;
  onChangeClick: (event: any) => void;
  isEmailConfirmed: boolean;
  hintText?: string;
  resendCooldownSec?: number;
  verifyCooldownSec?: number;
  formatMmSs?: (s: number) => string;
  verifyErrorText?: string;
}

function fallbackFormatMmSs(totalSec: number) {
  const s = Math.max(0, Math.floor(totalSec));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  const ss2 = ss < 10 ? `0${ss}` : String(ss);
  return `${mm}:${ss2}`;
}

function getStatus(isConfirmed: boolean, isLocked: boolean, resendCooldownSec: number, verifyCooldownSec: number) {
  if (isConfirmed) return { key: 'verified', text: 'Verified' as const };
  if (isLocked) return { key: 'waiting', text: 'Waiting' as const };
  return { key: 'pending', text: 'Code sent' as const };
}

export function PhoneVerificationForm(props: Props) {
  const {
    onSubmit,
    isConfirmed,
    phone,
    onSendCodeAgainClick,
    onChangeClick,
    isEmailConfirmed,
    hintText,
    resendCooldownSec,
    verifyCooldownSec,
    formatMmSs,
    verifyErrorText
  } = props;

  const resendCooldown = Number(resendCooldownSec || 0) || 0;
  const verifyCooldown = Number(verifyCooldownSec || 0) || 0;
  const isResendCooldown = resendCooldown > 0;
  const isVerifyCooldown = verifyCooldown > 0;
  const fmt = formatMmSs || fallbackFormatMmSs;
  const isLocked = !isEmailConfirmed && !isConfirmed;
  const status = getStatus(!!isConfirmed, isLocked, resendCooldown, verifyCooldown);

  const Schema = Yup.object().shape({
    phoneCode: Yup.string().required(
      isLocked
        ? 'Please verify your email first. SMS verification will unlock automatically once your email is confirmed.'
        : 'Please enter your phone verification code.'
    )
  });

  return (
    <section className={`eVerifyCard ${isLocked ? 'mLocked' : ''} ${isConfirmed ? 'mConfirmed' : ''}`}>
      <header className="eVerifyCardHeader">
        <div className="eVerifyCardHeaderLeft">
          <div className="eVerifyCardTitle">Mobile</div>
          <div className="eVerifyCardSubTitle">
            {isLocked ? <>{hintText || 'Verify your email first to unlock SMS verification.'}</> : <>{phone}</>}
          </div>
        </div>

        <span className={`eStatusPill m-${status.key}`}>
          <span className="eStatusDot" />
          {status.text}
        </span>
      </header>

      {isConfirmed ? (
        <div className="eVerifyCardBody">
          <div className="eVerifyConfirmedBox">
            <span className="eVerifyConfirmedIcon">✓</span>
            <div>
              <div className="eVerifyConfirmedTitle">Phone verified</div>
              <div className="eVerifyConfirmedText">Your phone number has been confirmed successfully.</div>
            </div>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={{ phoneCode: '' }}
          validationSchema={Schema}
          onSubmit={values => {
            if (isLocked || isVerifyCooldown) return;
            const code = String(values.phoneCode || '').trim();
            onSubmit({ phoneCode: code });
          }}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {({ touched, errors, values, submitCount, setFieldValue }) => {
            return (
              <Form className="eVerifyCardBody">
                <div className="eVerifyInputRow">
                  <VerificationCodeInput
                    disabled={isLocked}
                    label="Phone verification code"
                    value={values.phoneCode}
                    onChange={code => setFieldValue('phoneCode', code)}
                  />
                  <button className="eVerifyPrimaryBtn" disabled={isLocked || isVerifyCooldown} type="submit">
                    {isVerifyCooldown ? `Verify mobile (${fmt(verifyCooldown)})` : 'Verify mobile'}
                  </button>
                </div>

                {(touched.phoneCode || submitCount > 0) && errors.phoneCode && (
                  <div className="eVerifyInlineError">{String(errors.phoneCode)}</div>
                )}

                {!isLocked && !!verifyErrorText && verifyCooldown <= 0 && (
                  <div className="eVerifyInlineError">{verifyErrorText}</div>
                )}

                {!isLocked && (
                  <div className="eVerifyMetaRow">
                    <button
                      className="eVerifyLinkBtn"
                      type="button"
                      onClick={e => {
                        if (isResendCooldown) {
                          e.preventDefault();
                          return;
                        }
                        onSendCodeAgainClick(e);
                      }}
                      disabled={isResendCooldown}
                    >
                      {isResendCooldown ? `Send code again (${fmt(resendCooldown)})` : 'Send code again'}
                    </button>
                    <span className="eVerifyMetaSep">•</span>
                    <button className="eVerifyLinkBtn" type="button" onClick={onChangeClick}>
                      Wrong phone?
                    </button>
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      )}
    </section>
  );
}
